<template>
  <div class="notfound-page">
    <div class="notfound-glow"></div>
    <div class="notfound-blob notfound-blob-a"></div>
    <div class="notfound-blob notfound-blob-b"></div>
    <div class="notfound-blob notfound-blob-c"></div>

    <div class="notfound-card">
      <div class="notfound-icon-wrap">
        <span class="material-icons-round notfound-icon">timer_off</span>
      </div>

      <h1 class="notfound-title">{{ title }}</h1>
      <p class="notfound-message">{{ message }}</p>

      <button
        v-if="attemptedUrl"
        type="button"
        class="notfound-url"
        @click="copyUrl"
      >
        <span class="material-icons-round notfound-url-icon">link</span>
        <span class="notfound-url-body">
          <span class="notfound-url-label">Attempted URL</span>
          <code class="notfound-url-value">{{ attemptedUrl }}</code>
        </span>
        <span class="notfound-url-copy" :class="{ copied }">
          <span class="material-icons-round">{{ copied ? "check" : "content_copy" }}</span>
        </span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: "PageExpired",
  data() {
    return {
      copied: false,
    };
  },
  computed: {
    errorInfo() {
      return this.$store.state.error || {};
    },
    title() {
      return this.errorInfo.statusCode ? String(this.errorInfo.statusCode) : "419";
    },
    message() {
      return (
        this.errorInfo.message ||
        "Table has expired! Please contact staff for a new QR code."
      );
    },
    // returnPath is always domain-less (Vue Router's to.fullPath is a bare
    // path), so there's no host to leak — but mask a domain placeholder
    // anyway so this reads unambiguously as "not the real host" if it's
    // ever screenshotted or shared for support.
    attemptedUrl() {
      if (!this.errorInfo.returnPath) {
        return "";
      }
      return `*****${this.errorInfo.returnPath}`;
    },
  },
  methods: {
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.attemptedUrl);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 1500);
      } catch (e) {
        // Clipboard API unavailable/blocked — the text is already visible
        // and selectable, so there's nothing else useful to do here.
      }
    },
  },
};
</script>

<style scoped>
.notfound-page {
  --nf-accent: #f9dd4b;
  --nf-accent-dark: color-mix(in srgb, var(--nf-accent) 80%, black);
  --nf-accent-soft: color-mix(in srgb, var(--nf-accent) 15%, white);
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #f6f6fb;
  overflow: hidden;
}

.notfound-glow {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    60% 50% at 50% 38%,
    color-mix(in srgb, var(--nf-accent) 12%, transparent),
    transparent 70%
  );
  pointer-events: none;
}

.notfound-blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.32;
  pointer-events: none;
}

.notfound-blob-a {
  width: 380px;
  height: 380px;
  top: -130px;
  left: -110px;
  background: var(--nf-accent);
}

.notfound-blob-b {
  width: 320px;
  height: 320px;
  bottom: -110px;
  right: -90px;
  background: color-mix(in srgb, var(--nf-accent) 55%, white);
}

.notfound-blob-c {
  width: 240px;
  height: 240px;
  top: 42%;
  left: 58%;
  background: color-mix(in srgb, var(--nf-accent) 35%, white);
  opacity: 0.22;
}

.notfound-card {
  position: relative;
  width: 100%;
  max-width: 440px;
  text-align: center;
  padding: 56px 40px;
  background: #ffffff;
  border-radius: 28px;
  box-shadow: 0 30px 70px -24px color-mix(in srgb, var(--nf-accent) 28%, transparent),
    0 4px 16px rgba(17, 17, 17, 0.05);
  animation: notfound-rise 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

.notfound-icon-wrap {
  position: relative;
  width: 100px;
  height: 100px;
  margin: 0 auto 28px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--nf-accent-soft), #ffffff);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--nf-accent) 12%, transparent);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notfound-icon {
  font-size: 46px;
  color: var(--nf-accent);
}

.notfound-title {
  font-size: 30px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #16161d;
  margin: 0 0 12px;
}

.notfound-message {
  font-size: 15px;
  color: #6b6b78;
  line-height: 1.6;
  margin: 0;
}

.notfound-url {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin: 28px 0 0;
  padding: 12px 14px;
  border-radius: 14px;
  background: #f7f7fb;
  border: 1px solid #ececf3;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.notfound-url:hover {
  background: #f0f0f7;
  border-color: #e0e0ec;
}

.notfound-url-icon {
  flex: none;
  font-size: 18px;
  color: #a3a3b0;
}

.notfound-url-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.notfound-url-label {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #adadb8;
}

.notfound-url-value {
  display: block;
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 12.5px;
  color: #4b4b58;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notfound-url-copy {
  flex: none;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a3a3b0;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.notfound-url-copy .material-icons-round {
  font-size: 16px;
}

.notfound-url-copy.copied {
  background: #dcfce7;
  color: #16a34a;
}

@keyframes notfound-rise {
  from {
    opacity: 0;
    transform: translateY(16px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@media (max-width: 420px) {
  .notfound-card {
    padding: 44px 24px;
  }
}
</style>
